import React, {useEffect, useRef, useState} from 'react'
import {Container, Form, InputGroup} from 'react-bootstrap'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {collection, doc, writeBatch} from 'firebase/firestore'
import {useAuth} from 'app/modules/auth'
import db from 'services/firebase'
import {KTIcon, toAbsoluteUrl} from '_metronic/helpers'
import { showErrorToast, showSuccessToast } from 'app/components/ShowToast'

export const TrialOnboardingPage = () => {
  const [currentStep, setCurrentStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const {currentUser} = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)
  const searchRef = useRef<HTMLDivElement>(null)
  const [integration, setIntegration] = useState<any[]>([])

  const initialValues = {
    companyName: '',
    companyAddress: '',
    websiteUrl: '',
    companyLocation: '',
    userCount: '',
    frameworks: [] as string[],
    integrations: [] as string[],
    heardFrom: '',
    heardFromOther: '',
  }

  const validationSchema = Yup.object().shape({
    companyName: Yup.string().required('Company name is required'),
    websiteUrl: Yup.string().url('Please enter a valid URL').required('Website URL is required'),
    companyLocation: Yup.string().required('Company location is required'),
    userCount: Yup.string().required('Please select company size'),
    frameworks: Yup.array().min(1, 'Please select at least one framework'),
    integrations: Yup.array(),
    heardFrom: Yup.string().required('Please tell us how you heard about us'),
    heardFromOther: Yup.string().when(['heardFrom'], {
      is: (heardFrom: string) => heardFrom === 'other',
      then: (schema) => schema.required('Please specify where you heard about us'),
      otherwise: (schema) => schema.notRequired(),
    }),
  })

  useEffect(() => {
    getIntegrations()
  }, [])

  const getIntegrations = () => {
    const collectionRef = db.collection('integrations')

    const unsubscribe = collectionRef.onSnapshot((snapshot) => {
      const integrations: any[] = []
      snapshot.forEach((doc) => {
        const data = doc.data()
        integrations.push(data)
      })
      setIntegration(integrations)
    })
  }

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchFocused(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const tenantId = currentUser?.tenantId || ''
        const uid = currentUser?.uid || ''
        const batch = writeBatch(db)

        // Tenant document
        const tenantRef = doc(db, 'tenants', tenantId)
        batch.set(tenantRef, {})

        // User document update
        const userRef = doc(db, 'users', uid)
        batch.update(userRef, {
          companyName: values.companyName,
          websiteUrl: values.websiteUrl,
          companyLocation: values.companyLocation,
          companySize: values.userCount,
          heardFrom: values.heardFrom,
          heardFromOther: values.heardFromOther,
          updatedAt: new Date(),
        })

        const frameworkRef = doc(collection(tenantRef, 'active_frameworks'))
        batch.set(frameworkRef, {
          frameworks: values.frameworks,
        })

        const integrationsRef = doc(collection(tenantRef, 'active_integrations'))
        batch.set(integrationsRef, {integrations: ['GitHub', 'Github Dependabot', 'Google Cloud Platform', 'Slack', 'Google Workspace']})

        await batch.commit()
        window.location.href = '/dashboard'
        showSuccessToast({message: 'Setup completed successfully'})
      } catch (error) {
        showErrorToast({message: 'Failed to complete setup. Please try again.'})
      } finally {
        setLoading(false)
      }
    },
  })

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  }

  const toggleIntegration = (integrationId: string) => {
    const currentIntegrations = formik.values.integrations || []
    if (currentIntegrations.includes(integrationId)) {
      formik.setFieldValue(
        'integrations',
        currentIntegrations.filter((id) => id !== integrationId)
      )
    } else {
      formik.setFieldValue('integrations', [...currentIntegrations, integrationId])
    }
  }

  // useEffect(()=>{
  //   formik.setFieldValue('integrations', intialSelectedIntegrations)
  // },[intialSelectedIntegrations])

  const removeIntegration = (integrationId: string) => {
    const currentIntegrations = formik.values.integrations || []
    formik.setFieldValue(
      'integrations',
      currentIntegrations.filter((id) => id !== integrationId)
    )
  }

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const filteredIntegrations = integration.filter((integration) =>
    integration.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div
            className='d-flex flex-column justify-content-center h-100'
            // style={{minHeight: '50vh'}}
          >
            <h2 className='fw-bold fs-1'>Welcome to SecureSlate!</h2>
            <p className='text-gray-800 mb-0 fs-6'>
              Let's get started by getting to know your company and requirements better to
              personalize experience. This will only take a few minutes.
            </p>
            <div className='mt-12'>
              <h5 className='fw-bold mb-8 fs-4'>Company Details</h5>
              <div className='mb-4'>
                <label className='form-label fs-7'>Company Name</label>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  placeholder='secureslate'
                  // autoFocus
                  {...formik.getFieldProps('companyName')}
                />
                {formik.touched.companyName && formik.errors.companyName && (
                  <div className='text-danger fs-7'>{formik.errors.companyName}</div>
                )}
              </div>

              <div className='mb-4'>
                <label className='form-label fs-7'>Company Website</label>
                <input
                  type='url'
                  className='form-control form-control-sm'
                  placeholder='https://example.com'
                  {...formik.getFieldProps('websiteUrl')}
                />
                {formik.touched.websiteUrl && formik.errors.websiteUrl && (
                  <div className='text-danger fs-7'>{formik.errors.websiteUrl}</div>
                )}
              </div>
              <div className='mb-4'>
                <label className='form-label fs-7'>Where is your Company based?</label>
                <input
                  type='text'
                  className='form-control form-control-sm'
                  placeholder='UK'
                  {...formik.getFieldProps('companyLocation')}
                />
                {formik.touched.companyLocation && formik.errors.companyLocation && (
                  <div className='text-danger fs-7'>{formik.errors.companyLocation}</div>
                )}
              </div>
              <div className='mb-4'>
                <label className='form-label fs-7'>Total number of employees?</label>
                <div className='row g-4'>
                  {[
                    {label: '0-20', value: '0-20'},
                    {label: '20-50', value: '20-50'},
                    {label: '50-100', value: '50-100'},
                    {label: '100+', value: '100+'},
                  ].map(({label, value}) => (
                    <div className='col-12 col-md-3' key={value}>
                      <button
                        className={`btn btn-lg btn-outline w-100 ${
                          formik.values.userCount === value
                            ? 'border-dark bg-gray-100'
                            : 'border-gray-300 text-gray-700'
                        } px-4 py-2`}
                        style={{
                          transition: 'all 0.3s ease',
                          color: formik.values.userCount === value ? 'black' : '',
                          backgroundColor: formik.values.userCount === value ? '#f8f9fa' : '',
                        }}
                        onMouseOver={(e) => {
                          if (formik.values.userCount !== value) {
                            e.currentTarget.style.backgroundColor = '#f8f9fa'
                          }
                        }}
                        onMouseOut={(e) => {
                          if (formik.values.userCount !== value) {
                            e.currentTarget.style.backgroundColor = ''
                          }
                        }}
                        onClick={() => {
                          formik.setFieldValue('userCount', value)
                        }}
                      >
                        {label}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )

      case 2:
        return (
          <>
            <div
              className='d-flex flex-column justify-content-center h-100'
              // style={{minHeight: '50vh'}}
            >
              <h2 className='fw-bold fs-1'>Choose your first framework</h2>
              <p className='text-gray-800 mb-0 fs-6'>
                Please select the most important framework or regulation for you at this time. You
                can always choose to add additional frameworks later.
              </p>

              <div className='container mt-12 p-0'>
                <h5 className='fw-bold mb-8 fs-4'>Frameworks</h5>
                <div className='row row-cols-1 row-cols-md-4 g-4 justify-content-center'>
                  {[
                    {
                      label: 'ISO 27001',
                      value: 'ISO 27001:2022',
                      img: '/media/framework/ISO27001.svg',
                    },
                    {label: 'GDPR', value: 'GDPR', img: '/media/framework/GDPR.svg'},
                    {label: 'SOC 2', value: 'SOC 2', img: '/media/framework/SOC2.svg'},
                    {label: 'HIPAA ', value: 'HIPAA', img: '/media/framework/HIPAA.svg'},
                  ].map(({label, value, img}) => (
                    <div className='col-12 col-md-4' key={value}>
                      <button
                        className={`btn btn-lg btn-outline w-100 ${
                          formik.values.frameworks?.includes(value)
                            ? 'border-dark bg-gray-100'
                            : 'border-gray-300 text-gray-700'
                        } px-4 py-2`}
                        style={{
                          transition: 'all 0.3s ease',
                          color: formik.values.frameworks?.includes(value) ? 'black' : '',
                          backgroundColor: formik.values.frameworks?.includes(value)
                            ? '#f8f9fa'
                            : '',
                        }}
                        onMouseOver={(e) => {
                          if (!formik.values.frameworks?.includes(value)) {
                            e.currentTarget.style.backgroundColor = '#f8f9fa'
                          }
                        }}
                        onMouseOut={(e) => {
                          if (!formik.values.frameworks?.includes(value)) {
                            e.currentTarget.style.backgroundColor = ''
                          }
                        }}
                        onClick={() => {
                          formik.setFieldValue('frameworks', [value])
                        }}
                      >
                          <div className='d-flex align-items-center justify-content-center'>
                        <img
                          src={img}
                          alt={label}
                          className='me-2'
                          style={{width: '16px', height: '16px', objectFit: 'contain'}}
                        />{' '}
                        <span className='fs-6'> {label}</span>
                        </div>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )

      case 3:
        return (
          <>
            <div
              className='d-flex flex-column justify-content-center h-100'
              // style={{minHeight: '50vh'}}
            >
              <h2 className='fw-bold fs-1'>Your integrations</h2>
              <p className='text-gray-800 mb-0 fs-6'>
                Select your current tools and systems that needs to be integrated with SecureSlate
                for monitoring and evidence collection.
              </p>
              <div className='mb-4 mt-8'>
                <h5 className='fw-bold mb-5'>Integrations</h5>

                <Form.Group className='mb-3 position-relative' ref={searchRef}>
                  <Form.Label>Select Integration</Form.Label>
                  <InputGroup>
                    <div className='w-100 d-flex align-items-center position-relative my-1'>
                      <KTIcon iconName='magnifier' className='fs-3 position-absolute ms-6' />
                      <input
                        type='text'
                        data-kt-user-table-filter='search'
                        placeholder='Search...'
                        value={searchTerm}
                        onChange={handleSearchChange}
                        onFocus={() => setIsSearchFocused(true)}
                        className='form-control form-control-sm ps-14 w-100 min-vw-80'
                        style={{
                          borderRadius: '8px',
                          height: '36px',
                        }}
                      />
                      {searchTerm && (
                        <button
                          className='btn btn-sm btn-icon position-absolute top-50 end-0 translate-middle-y me-2 p-1'
                          onClick={() => setSearchTerm('')}
                          style={{
                            background: 'transparent',
                            transition: 'all 0.2s ease',
                            width: '24px',
                            height: '24px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px'
                          }}
                          onMouseOver={(e) => {
                            e.currentTarget.style.backgroundColor = '#f8f9fa'
                          }}
                          onMouseOut={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent'
                          }}
                        >
                          <i className='bi bi-x fs-2 text-gray-900'></i>
                        </button>
                      )}
                    </div>
                  </InputGroup>

                  {/* Dropdown for search results */}
                  {isSearchFocused && searchTerm && (
                    <div
                      className='position-absolute w-100 mt-1 border rounded bg-white shadow-sm z-index-dropdown'
                      style={{zIndex: 1000, maxHeight:'175px',overflowY:'auto'
                      }}
                    >
                      {filteredIntegrations.length > 0 ? (
                        filteredIntegrations.map((integration,idx) => (
                          <div
                            key={`${integration.name}-${idx}`}
                            className='d-flex align-items-center p-2 border-bottom'
                            onClick={(e) => {
                              e.stopPropagation() // Prevent click from bubbling up
                              toggleIntegration(integration.name)
                            }}
                          >
                            <div className='d-flex align-items-center gap-2 w-100'>
                              <Form.Check
                                type='checkbox'
                                id={`integration-${integration.name}`}
                                checked={formik.values.integrations.includes(integration.name)}
                                onChange={() => {}} // Handled by parent div click
                                className='m-0'
                                style={
                                  {
                                    '--bs-form-check-min-height': '1rem',
                                    '--bs-form-check-margin-bottom': '0.125rem',
                                    transform: 'scale(0.65)',
                                    transformOrigin: 'left center',
                                  } as React.CSSProperties
                                }
                              />
                              <span className='d-flex align-items-center'>
                                <img
                                  src={integration.img}
                                  alt={integration.name}
                                  className='me-2'
                                  style={{width: '16px', height: '16px', objectFit: 'contain'}}
                                />
                                <span className='fw-semibold text-dark'>{integration.name}</span>
                              </span>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className='text-center p-3'>
                          <p className='text-muted mb-0'>
                            No integrations found matching "{searchTerm}"
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {formik.values.integrations.length > 0 && (
                    <div className='mb-3 mt-4'>
                      <Form.Label>Selected</Form.Label>
                      <div className='d-flex flex-wrap gap-2'>
                        {formik.values.integrations.map((name) => {
                          const integrationData = integration.find((i) => i.name === name)
                          if (!integrationData) return null

                          return (
                            <button
                              key={name}
                              className='d-flex align-items-center btn btn-sm btn-outline border-dark text-dark fw-semibold px-3'
                              onClick={() => removeIntegration(name)}
                            >
                              <img
                                src={integrationData.img}
                                alt={integrationData.name}
                                className='me-2'
                                style={{width: '16px', height: '16px', objectFit: 'contain'}}
                              />
                              {integrationData.name}
                              <i className='bi bi-x ms-2 fs-2 text-dark'></i>
                            </button>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </Form.Group>
                
                  {formik.values.integrations.length === 0 && (
                    <div className='text-center mt-6'>
                      <button
                        type='button'
                        className='btn btn-link text-muted fs-7 text-decoration-none'
                        onClick={() => {
                          formik.setFieldValue('integrations', [])
                          setCurrentStep(currentStep + 1)
                        }}
                        style={{
                          transition: 'all 0.3s ease',
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.color = '#6c757d'
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.color = '#6c757d'
                        }}
                      >
                        Skip for now, I'll add integrations later
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </>
        )

      case 4:
        return (
          <div
            className='d-flex flex-column justify-content-center h-100'
            // style={{minHeight: '50vh'}}
          >
            <h2 className='fw-bold fs-1'>You are all set</h2>
            <p className='text-gray-800 mb-0 fs-6'>
              You can now start to build your compliance program on SecureSlate. Before you start,
              we'd love to know how you got know about us.
            </p>
            <div className='container mt-12 p-0'>
              <h5 className='fw-bold mb-8 fs-4'>Where did you hear about us?</h5>
              <div className='row row-cols-1 row-cols-md-3 g-4 justify-content-center'>
                {[
                  {label: 'LinkedIn', value: 'linkedin'},
                  {label: 'Reddit', value: 'reddit'},
                  {label: 'Google', value: 'google'},
                  {label: 'Twitter', value: 'twitter'},
                  {label: 'Blog', value: 'blog'},
                  {label: 'Other', value: 'other'},
                ].map(({label, value}) => (
                  <div className='col-12 col-md-4 mb-2' key={value}>
                    <button
                      className={`btn btn-lg btn-outline w-100 ${
                        formik.values.heardFrom === value
                          ? 'border-dark bg-gray-100'
                          : 'border-gray-300 text-gray-700'
                      } px-4 py-2`}
                      style={{
                        transition: 'all 0.3s ease',
                        color: formik.values.heardFrom === value ? 'black' : '',
                        backgroundColor: formik.values.heardFrom === value ? '#f8f9fa' : '',
                      }}
                      onMouseOver={(e) => {
                        if (formik.values.heardFrom !== value) {
                          e.currentTarget.style.backgroundColor = '#f8f9fa'
                        }
                      }}
                      onMouseOut={(e) => {
                        if (formik.values.heardFrom !== value) {
                          e.currentTarget.style.backgroundColor = ''
                        }
                      }}
                      onClick={() => {
                        formik.setFieldValue('heardFrom', value)
                      }}
                    >
                      {label}
                    </button>
                  </div>
                ))}
              </div>
              {formik.values.heardFrom === 'other' && (
                <div className='mt-4'>
                  <label className='form-label fs-7'>Please specify where you heard about us</label>
                  <input
                    type='text'
                    className='form-control form-control-sm'
                    placeholder='Please specify'
                    {...formik.getFieldProps('heardFromOther')}
                  />
                  {formik.touched.heardFromOther && formik.errors.heardFromOther && (
                    <div className='text-danger fs-7'>{formik.errors.heardFromOther}</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )
    }
  }

  const handleNext = () => {
    if (currentStep === 4) {
      formik.submitForm()
      return
    }
    setCurrentStep(currentStep + 1)
  }

  const isNextDisabled = (): boolean => {
    switch (currentStep) {
      case 1:
        return Boolean(
          !formik.values.companyName ||
          !formik.values.websiteUrl ||
          !formik.values.companyLocation ||
          !formik.values.userCount ||
          formik.errors.companyName ||
          formik.errors.websiteUrl ||
          formik.errors.companyLocation
        )
      case 2:
        return !formik.values.frameworks?.length
      case 3:
        return false
      case 4:
        return Boolean(
          !formik.values.heardFrom ||
          (formik.values.heardFrom === 'other' && !formik.values.heardFromOther)
        )
      default:
        return false
    }
  }

  const renderProgressBar = () => {
    return (
      <div className='my-4'>
        <div className='d-flex gap-2'>
          {[1, 2, 3, 4].map((step) => (
            <div
              key={step}
              className={`flex-grow-1 rounded ${step <= currentStep ? 'bg-dark' : 'bg-gray-200'}`}
              style={{
                height: '4px',
                transition: 'all 0.3s ease',
              }}
            ></div>
          ))}
        </div>
        <p className='text-gray-800 fs-7 mt-4 mb-0'>Step {currentStep} of 4</p>
      </div>
    )
  }

  return (
    <Container
    className='d-flex justify-content-center align-items-center py-4'
  >
      <div 
        className='p-4 p-sm-6 p-md-8 rounded-3 w-100' 
        style={{
          maxWidth: '600px',
          minHeight: '85vh',
          overflowY: 'auto'
        }}
      >
        <div className='d-inline-block bg-gray-200 p-2 rounded-2 mb-12'>
          <img
            alt='Logo'
            src={toAbsoluteUrl('/Secureslate_logos/Secureslate_dark.png')}
            className='h-20px app-sidebar-logo-default'
          />
        </div>

        <div className='flex-grow-1 mb-8'
        style={{minHeight: '50vh'}}
        >
          {renderStep()}
        </div>

        <div className='mt-auto'>
          {renderProgressBar()}
          <div className='d-flex flex-column flex-sm-row justify-content-between gap-3 align-items-center'>
            <button
              type='button'
              className='btn btn-outline border-gray-400 btn-sm fs-6 d-flex align-items-center gap-2'
              style={{
                borderRadius: '8px',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#e9ecef'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = ''
              }}
              onClick={handleBack}
              disabled={currentStep === 1 || loading}
            >
              <svg
                width='16'
                height='16'
                viewBox='0 0 16 16'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path
                  d='M10.0334 3.33337L5.36675 8.00004L10.0334 12.6667'
                  stroke='currentColor'
                  strokeWidth='1.5'
                  strokeLinecap='round'
                  strokeLinejoin='round'
                />
              </svg>
              Back
            </button>
            <button
              type='submit'
              className='btn btn-sm fs-6 d-flex align-items-center gap-2'
              style={{
                background: '#0E9F6E',
                borderRadius: '8px',
                color: 'white',
                transition: 'all 0.3s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.backgroundColor = '#0b8f61'
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.backgroundColor = '#0E9F6E'
              }}
              onClick={(e) => {
                e.preventDefault()
                if (currentStep === 4) {
                  formik.handleSubmit()
                } else {
                  handleNext()
                }
              }}
              disabled={isNextDisabled() || loading}
            >
              {currentStep === 4 ? (
                loading ? (
                  <>
                    <span
                      className='spinner-border spinner-border-sm me-2'
                      role='status'
                      aria-hidden='true'
                    ></span>
                    Setting up...
                  </>
                ) : (
                  'Get Started'
                )
              ) : (
                <>
                  Next
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      d='M5.96675 3.33337L10.6334 8.00004L5.96675 12.6667'
                      stroke='currentColor'
                      strokeWidth='1.5'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                  </svg>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </Container>
  )
}
